import { Component, OnDestroy } from '@angular/core';
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community';
import FileSaver from 'file-saver';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FileTypeConstant } from '@app/core/_constant/index.constants';
import { LoadingDialogUtilService } from 'v2/modules/shared/services/util/loading-dialog-util.service';

import { AssemblyOrderApiService } from '../../../shared/services/api/assembly-order-api.service';
import { AssemblyOrderConfig } from '../../configs/assembly-order.config';
import {
  EAssemblyOrderAgField,
  EAssemblyOrderType,
} from '../../interfaces/assembly-order.interface';
import { AssemblyOrderUtil } from '../../utils/assembly-order.util';

@Component({
  selector: 'lum-assembly-order-closed-orders-table',
  templateUrl: './assembly-order-closed-orders-table.component.html',
  styleUrls: ['./assembly-order-closed-orders-table.component.scss'],
})
export class AssemblyOrderClosedOrdersTableComponent implements OnDestroy {
  columnDefs :ColDef[] = AssemblyOrderConfig.openOrdersColumnDefs.map(
    (columnDef :ColDef) => {
      if (columnDef.field === EAssemblyOrderAgField.ACTIONS) {
        return {
          ...columnDef,
          cellRendererParams: {
            onExportOrder: (data :any) => this.onExportOrderEvent(data),
          },
        };
      }

      return columnDef;
    },
  );

  gridOptions :GridOptions = AssemblyOrderConfig.gridOptions;

  private gridApi :GridApi;

  private onDestroy$ = new Subject();

  constructor(
    private assemblyOrderApiService :AssemblyOrderApiService,
    private loadingDialogService :LoadingDialogUtilService,
  ) {}

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onGridReady(params :GridReadyEvent) :void {
    this.gridApi = params.api;
    const datasource = this.getServerSideDatasource();
    this.gridApi.setServerSideDatasource(datasource);
  }

  getServerSideDatasource() :IServerSideDatasource {
    return {
      getRows: (params :IServerSideGetRowsParams) => {
        this.assemblyOrderApiService
          .get(this.getRequestParams(params))
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((response) => {
            params.successCallback(response.data, response.meta.total);
          });
      },
    };
  }

  private onExportOrderEvent(data :any) :void {
    const type = data.type ?? EAssemblyOrderType.ASSEMBLY;
    const title =
      type === EAssemblyOrderType.ASSEMBLY ? 'Assembly Order' : 'Disassembly Order';

    const params = {
      exportType: FileTypeConstant.PDF,
      orderType: title,
    };
    this.loadingDialogService.show('Creating PDF, please wait...');
    this.assemblyOrderApiService.exportOrder(data.id, params).subscribe({
      next: (resp) => {
        FileSaver.saveAs(resp, title + '_' + data.id + '.pdf');
        this.loadingDialogService.close();
      },
      error: (resp) => {
        this.loadingDialogService.close();
        this.loadingDialogService.showDialogMessage('Error Export');
      },
    });
  }

  private getRequestParams(params :IServerSideGetRowsParams) :{
    [param :string] :string | string[];
  } {
    const limit = this.gridOptions.paginationPageSize || 10;
    const page = params?.request?.startRow
      ? params.request.startRow / limit + 1
      : 1;
    const defaultParams = {
      limit: limit.toString(),
      page: page.toString(),
      statuses: 'completed',
      includeAge: 'true',
      sortField: 'id',
      sortDirection: 'desc',
    };

    return AssemblyOrderUtil.mapRequestParams(params, defaultParams);
  }
}
